import {useState, useEffect, useCallback, useMemo} from 'react';
import {ShredResponse} from "@/services/MicroServiceService";
import {TourStep} from '../types/tourTypes';

interface TourState {
    currentStep: TourStep;
    isVisible: boolean;
    completedSteps: Set<TourStep>;
}

interface UseTourStateReturn {
    currentStep: TourStep;
    isVisible: boolean;
    completedSteps: Set<TourStep>;
    handleCloseTour: () => void;
    handleToggleTour: () => void;
    markStepCompleted: (step: TourStep) => void;
}

export const useTourState = (
    shredsArr: ShredResponse[],
    dataModelShred: ShredResponse | undefined,
    featureShreds: ShredResponse[],
    initialLoading: boolean,
    loading: boolean
): UseTourStateReturn => {
    const [tourState, setTourState] = useState<TourState>({
        currentStep: 'GENERATE_SHREDS' as TourStep,
        isVisible: false,
        completedSteps: new Set<TourStep>()
    });

    // Clear temporary closure flag on initial load
    useEffect(() => {
        sessionStorage.removeItem('tour-temporarily-closed');
    }, []);

    // Create stable dependency values using useMemo to prevent infinite loops
    const featureSummariesHash = useMemo(() =>
            featureShreds.map(s => s.summary || '').join('|'),
        [featureShreds]
    );

    const featureStatusesHash = useMemo(() =>
            featureShreds.map(s => s.status).join('|'),
        [featureShreds]
    );

    const dataModelHash = useMemo(() =>
            `${dataModelShred?.shredId || ''}-${dataModelShred?.summary || ''}-${dataModelShred?.status || ''}-${dataModelShred?.modelValidated || false}`,
        [dataModelShred]
    );

    // Determine what step we should be on based on current state
    const determineCurrentStep = useCallback((): TourStep => {
        // Step 1: No shreds exist
        if (shredsArr.length === 0) {
            return 'GENERATE_SHREDS';
        }

        // Step 2: No data model shred exists
        if (!dataModelShred) {
            return 'GENERATE_MODEL';
        }

        // Step 3: Data model exists but no specs
        if (!dataModelShred.summary) {
            return 'GENERATE_MODEL_SPECS';
        }

        // Step 4: Data model has specs but not validated
        if (!dataModelShred.modelValidated) {
            return 'EXPLORE_MODEL';
        }

        // Step 5: Data model validated but not completed
        if (!['DONE', 'STEP_DONE'].includes(dataModelShred.status)) {
            return 'WAIT_MODEL_VALIDATION';
        }

        // Step 6-8: Data model is complete, check features
        // FIXED: Check for ANY completed feature first (like the model logic)
        const featureWithCompletedSpecs = featureShreds.find(shred =>
            shred.summary && ['SPECS_GENERATED', 'DONE'].includes(shred.status)
        );

        // If ANY feature has completed specs, show explore (same as model behavior)
        if (featureWithCompletedSpecs) {
            return 'EXPLORE_FEATURE';
        }

        // Only if NO features have completed specs, check for features needing specs
        const featureWithoutSpecs = featureShreds.find(shred => !shred.summary);
        if (featureWithoutSpecs) {
            return 'GENERATE_FEATURE_SPECS';
        }

        // Final step: Everything is complete
        return 'COMPLETED';
    }, [
        shredsArr.length,
        dataModelHash,
        featureShreds.length,
        featureSummariesHash,
        featureStatusesHash
    ]);

    // Check if we should show the tour for the current step
    const shouldShowTour = useCallback((step: TourStep): boolean => {
        if (initialLoading) {
            return false;
        }

        if (step === 'GENERATE_SHREDS') {
            return !tourState.completedSteps.has(step);
        }

        if (loading) {
            return false;
        }

        if (tourState.completedSteps.has(step)) {
            return false;
        }

        return true;
    }, [initialLoading, loading, tourState.completedSteps]);

    // Update tour state when conditions change - FIXED LOGGING
    useEffect(() => {
        const newStep = determineCurrentStep();
        const shouldShow = shouldShowTour(newStep);

        // ONLY log and update when step actually changes
        if (newStep !== tourState.currentStep) {
            console.log(`Tour: Step change ${tourState.currentStep} → ${newStep}`);

            setTourState(prev => ({
                ...prev,
                currentStep: newStep,
                isVisible: shouldShow
            }));
        } else if (!tourState.isVisible && shouldShow && !sessionStorage.getItem('tour-temporarily-closed')) {
            // Only show tour if it's not temporarily closed
            setTourState(prev => ({
                ...prev,
                isVisible: true
            }));
        }
    }, [determineCurrentStep, shouldShowTour, tourState.currentStep, tourState.isVisible]);

    // Handle tour closure (temporary hide, not completion)
    const handleCloseTour = useCallback(() => {
        console.log('Tour: Temporarily closed');
        setTourState(prev => ({
            ...prev,
            isVisible: false
        }));

        sessionStorage.setItem('tour-temporarily-closed', 'true');
        setTimeout(() => {
            sessionStorage.removeItem('tour-temporarily-closed');
        }, 30000);
    }, []);

    // Handle tour toggle
    const handleToggleTour = useCallback(() => {
        const newVisibility = !tourState.isVisible;

        setTourState(prev => ({
            ...prev,
            isVisible: newVisibility
        }));

        if (newVisibility) {
            sessionStorage.removeItem('tour-temporarily-closed');
        } else {
            sessionStorage.setItem('tour-temporarily-closed', 'true');
            setTimeout(() => {
                sessionStorage.removeItem('tour-temporarily-closed');
            }, 30000);
        }
    }, [tourState.isVisible]);

    // Mark current step as completed
    const markStepCompleted = useCallback((step: TourStep) => {
        console.log(`Tour: Completed step ${step}`);
        setTourState(prev => ({
            ...prev,
            completedSteps: new Set([...prev.completedSteps, step]),
            isVisible: false
        }));
    }, []);

    // Check if tour should be temporarily hidden
    const isTemporarilyClosed = sessionStorage.getItem('tour-temporarily-closed') === 'true';
    const finalIsVisible = tourState.isVisible && !isTemporarilyClosed;

    return {
        currentStep: tourState.currentStep,
        isVisible: finalIsVisible,
        completedSteps: tourState.completedSteps,
        handleCloseTour,
        handleToggleTour,
        markStepCompleted
    };
};